import { Controller } from "@hotwired/stimulus"

// Debounced fetch of /bookings/calculate_fees on any form change.
// Renders the returned breakdown into the booking summary DOM.
export default class extends Controller {
  static targets = [
    "voucherCode",
    "subtotal", "platformFee", "total",
    "priorityFeeLabelRow", "priorityFeeAmountRow", "priorityFeeAmount", "priorityFeeTooltipIcon",
    "promoLabelRow", "promoAmountRow", "promoAmount",
    "sessionDuration", "sessionRate"
  ]
  static values = { url: String }

  connect() {
    this._onFormChange = this._onFormChange.bind(this)
    document.querySelectorAll(".service-type-radio").forEach(r =>
      r.addEventListener("change", this._onFormChange)
    )
    // start_time is outside this controller's scope (inside the time-slot div),
    // so Stimulus `data-action` routing doesn't reach us. Listen here instead.
    const startTimeEl = document.getElementById("start_time")
    if (startTimeEl) startTimeEl.addEventListener("change", this._onFormChange)
    this.recalc()
  }

  disconnect() {
    document.querySelectorAll(".service-type-radio").forEach(r =>
      r.removeEventListener("change", this._onFormChange)
    )
    const startTimeEl = document.getElementById("start_time")
    if (startTimeEl) startTimeEl.removeEventListener("change", this._onFormChange)
  }

  _onFormChange() { this.scheduleRecalc() }

  scheduleRecalc() {
    clearTimeout(this._t)
    this._t = setTimeout(() => this.recalc(), 150)
  }

  async recalc() {
    const serviceInput = document.querySelector(".service-type-radio:checked")
    // start_time is outside this controller's scope (inside the time-slot div),
    // so we read it directly by id rather than via a Stimulus target.
    const startTimeEl = document.getElementById("start_time")
    const startTime = startTimeEl ? startTimeEl.value : ""
    const voucherCode = this.hasVoucherCodeTarget ? this.voucherCodeTarget.value : ""

    if (!serviceInput || !startTime) {
      this.renderZero()
      return
    }

    try {
      const token = document.querySelector('meta[name="csrf-token"]').content
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        body: JSON.stringify({
          service_type_id: serviceInput.value,
          start_time: startTime,
          voucher_code: voucherCode
        })
      })
      if (!response.ok) { this.renderZero(); return }
      const data = await response.json()
      this.render(data, serviceInput.dataset)
    } catch (err) {
      console.error("fee calc failed:", err)
      this.renderZero()
    }
  }

  renderZero() {
    this.subtotalTarget.textContent = "RM 0.00"
    this.platformFeeTarget.textContent = "RM 0.00"
    this.totalTarget.textContent = "RM 0.00"
    this.priorityFeeLabelRowTarget.style.display = "none"
    this.priorityFeeAmountRowTarget.style.display = "none"
    this.promoLabelRowTarget.style.display = "none"
    this.promoAmountRowTarget.style.display = "none"
    this.sessionDurationTarget.textContent = "0 minutes"
    this.sessionRateTarget.textContent = "RM 0.00 / session"
  }

  render(data, serviceData) {
    const fmt = n => "RM " + Number(n).toFixed(2)
    this.subtotalTarget.textContent = fmt(data.subtotal)
    this.platformFeeTarget.textContent = fmt(data.platform_fee)
    this.totalTarget.textContent = fmt(data.total)
    this.sessionDurationTarget.textContent = (serviceData.duration || "0") + " minutes"
    this.sessionRateTarget.textContent = fmt(serviceData.price || 0) + " / session"

    const showPriority = Number(data.priority_fee) > 0
    this.priorityFeeLabelRowTarget.style.display = showPriority ? "" : "none"
    this.priorityFeeAmountRowTarget.style.display = showPriority ? "" : "none"
    if (showPriority) {
      this.priorityFeeAmountTarget.textContent = "+ " + fmt(data.priority_fee)
      if (this.hasPriorityFeeTooltipIconTarget) {
        const parts = []
        if (Number(data.peak_surcharge) > 0) parts.push(`Peak Hours: ${fmt(data.peak_surcharge)}`)
        if (Number(data.urgency_surcharge) > 0) parts.push(`Urgency: ${fmt(data.urgency_surcharge)}`)
        const title = parts.join(" · ")
        const el = this.priorityFeeTooltipIconTarget
        el.setAttribute("data-bs-title", title)
        document.dispatchEvent(new CustomEvent("tooltip:reinit", { detail: { el } }))
      }
    }

    const showPromo = Number(data.promo) > 0
    this.promoLabelRowTarget.style.display = showPromo ? "" : "none"
    this.promoAmountRowTarget.style.display = showPromo ? "" : "none"
    if (showPromo) this.promoAmountTarget.textContent = "- " + fmt(data.promo)
  }
}
